import { useLayoutEffect } from 'react';

/**
 * ניהול תגיות ה-head לפי הראוט, בלי תלות חיצונית.
 * כל שינוי נרשם ומוחזר לקדמותו כשעוזבים את הראוט, כך ששני העמודים
 * לא דורסים אחד את התגיות של השני בניווט פנימי.
 *
 * הערה: זחלני רשתות חברתיות (ווצאפ, פייסבוק) לא מריצים JavaScript.
 * עבורם ה-HTML הסטטי הוא מקור האמת - ראו scripts/postbuild-seo.js
 * שמייצר build/course/index.html עם התגיות של עמוד הקורס.
 */

function setMeta(undoList, attr, name, content) {
  if (content == null) return;
  const selector = `meta[${attr}="${name}"]`;
  const existing = document.head.querySelectorAll(selector);

  if (existing.length) {
    // משאירים את הראשונה ומסתירים כפילויות היסטוריות
    existing.forEach((el, i) => {
      const prev = el.getAttribute('content');
      if (i === 0) {
        el.setAttribute('content', content);
        undoList.push(() => el.setAttribute('content', prev));
      } else {
        el.remove();
        undoList.push(() => document.head.appendChild(el));
      }
    });
    return;
  }

  const el = document.createElement('meta');
  el.setAttribute(attr, name);
  el.setAttribute('content', content);
  document.head.appendChild(el);
  undoList.push(() => el.remove());
}

function setLink(undoList, rel, href) {
  if (!href) return;
  const existing = document.head.querySelector(`link[rel="${rel}"]`);
  if (existing) {
    const prev = existing.getAttribute('href');
    existing.setAttribute('href', href);
    undoList.push(() => existing.setAttribute('href', prev));
    return;
  }
  const el = document.createElement('link');
  el.setAttribute('rel', rel);
  el.setAttribute('href', href);
  document.head.appendChild(el);
  undoList.push(() => el.remove());
}

export default function useSeo(config) {
  const {
    title,
    description,
    keywords,
    canonical,
    image,
    url,
    type = 'website',
    siteName,
    lang = 'he',
    dir = 'rtl',
    jsonLd,
  } = config;

  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useLayoutEffect(() => {
    const undo = [];
    const html = document.documentElement;

    const prevTitle = document.title;
    if (title) {
      document.title = title;
      undo.push(() => {
        document.title = prevTitle;
      });
    }

    const prevLang = html.getAttribute('lang');
    const prevDir = html.getAttribute('dir');
    html.setAttribute('lang', lang);
    html.setAttribute('dir', dir);
    undo.push(() => {
      if (prevLang) html.setAttribute('lang', prevLang);
      else html.removeAttribute('lang');
      if (prevDir) html.setAttribute('dir', prevDir);
      else html.removeAttribute('dir');
    });

    setMeta(undo, 'name', 'description', description);
    setMeta(undo, 'name', 'keywords', keywords);
    setMeta(undo, 'property', 'og:title', title);
    setMeta(undo, 'property', 'og:description', description);
    setMeta(undo, 'property', 'og:image', image);
    setMeta(undo, 'property', 'og:url', url || canonical);
    setMeta(undo, 'property', 'og:type', type);
    setMeta(undo, 'property', 'og:site_name', siteName);
    setMeta(undo, 'property', 'og:locale', 'he_IL');
    setMeta(undo, 'name', 'twitter:card', 'summary_large_image');
    setMeta(undo, 'name', 'twitter:title', title);
    setMeta(undo, 'name', 'twitter:description', description);
    setMeta(undo, 'name', 'twitter:image', image);
    setLink(undo, 'canonical', canonical);

    if (jsonLdKey) {
      document.head
        .querySelectorAll('script[type="application/ld+json"][data-route-seo]')
        .forEach((el) => el.remove());
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-route-seo', '');
      script.textContent = jsonLdKey;
      document.head.appendChild(script);
      undo.push(() => script.remove());
    }

    return () => {
      for (let i = undo.length - 1; i >= 0; i -= 1) undo[i]();
    };
  }, [
    title,
    description,
    keywords,
    canonical,
    image,
    url,
    type,
    siteName,
    lang,
    dir,
    jsonLdKey,
  ]);
}
